import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function AffiliateDisclosurePage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Affiliate Disclosure | Best Christmas Gift Reviews';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Learn how Best Christmas Gift Reviews earns commissions through affiliate links and how our editorial independence is maintained.');

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/affiliate-disclosure#webpage`,
      url: `${SITE_URL}/affiliate-disclosure`,
      name: 'Affiliate Disclosure',
      description: 'Learn how Best Christmas Gift Reviews earns commissions through affiliate links and how our editorial independence is maintained.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Affiliate Disclosure', item: `${SITE_URL}/affiliate-disclosure` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-affiliate';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('jsonld-affiliate');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-[#1B4332] hover:text-[#8B2635] transition-colors mb-6">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-[#1B4332] mb-4">Affiliate Disclosure</h1>
            <p className="text-gray-500">Last Updated: January 2025</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Our Commitment to Transparency</h2>
              <p className="text-gray-700 leading-relaxed">
                At Best Christmas Gift Reviews, we believe in complete transparency with our readers. This disclosure explains how we earn money and how it affects the content you see on our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Amazon Associates Program</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Best Christmas Gift Reviews is a participant in the Amazon Services LLC Associates Program, an affiliate advertising program designed to provide a means for sites to earn advertising fees by advertising and linking to Amazon.com.
              </p>
              <p className="text-gray-700 leading-relaxed">
                As an Amazon Associate, we earn from qualifying purchases. This means when you click on a link to Amazon from our site and make a purchase, we may receive a small commission at no additional cost to you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">How Affiliate Links Work</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>When you click on product links on our site, you may be directed to Amazon or other retail partners.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>If you make a purchase through these links, we receive a small percentage of the sale.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>The price you pay remains the same whether you use our affiliate link or not.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#D4AF37] mt-1"></i>
                  <span>Commissions help us maintain this website and continue providing free content.</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Editorial Independence</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our reviews and recommendations are based on genuine research, testing, and analysis. Affiliate relationships do not influence our editorial content or product ratings.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We only recommend products we believe will provide value to our readers. Our reputation depends on providing honest, unbiased reviews, and we take that responsibility seriously.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1B4332] mb-4">Other Affiliate Partnerships</h2>
              <p className="text-gray-700 leading-relaxed">
                In addition to Amazon, we may participate in other affiliate programs. Any such relationships will be disclosed, and the same principles of transparency and editorial independence apply.
              </p>
            </section>

            <section className="bg-[#FFF9F5] rounded-xl p-6 border border-[#D4AF37]/20">
              <h2 className="text-xl font-bold text-[#1B4332] mb-3">Questions?</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about our affiliate relationships or this disclosure, please contact us at{' '}
                <a href="mailto:hello@bestchristmasgiftreviews.com" className="text-[#8B2635] hover:underline">
                  hello@bestchristmasgiftreviews.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
